import type { Player, Team } from './api';

/**
 * The squad list, as a document.
 *
 * The league circulates a squad list — every team, every player, every seed —
 * and until now that list lived in a PDF somebody typed. The database drifted
 * from it: four seeds and two whole squad places, found by comparing all 109
 * players rather than by anybody noticing. The list was right and the database
 * was wrong, which is the more dangerous way round, because the list is what
 * captains read and the database is what the app scores from.
 *
 * So this builds the list FROM the season the app is running. It cannot
 * disagree with the site, because it is the site. That is the whole point of
 * it, and it is why nothing here takes a hand-typed name or number.
 *
 * Pure, and separate from the page that draws it, so the arithmetic that
 * matters — squad totals against the cap — is testable without a browser.
 */

/** Society's combined maximum for a squad. Null for leagues that have none. */
export const SOCIETY_SQUAD_CAP = 24;

export interface ListedPlayer {
  /** Position in the whole list, 1…N, so a player can be pointed at by number. */
  number: number;
  name: string;
  /** The seed. Null for a player who has not been rated. */
  rating: number | null;
  /** The squad tier, in leagues that grade rather than rate. Null otherwise. */
  tier: string | null;
  /** A one-off substitute, who belongs to no ongoing squad. */
  substitute: boolean;
}

export interface ListedTeam {
  id: number;
  name: string;
  logoUrl: string | null;
  colour: string | null;
  players: ListedPlayer[];
  /**
   * The squad's combined seed.
   *
   * Only counts players who HAVE a seed, and `unrated` says how many were left
   * out. A total that silently treats an unrated player as zero would read as
   * comfortable room under the cap when the truth is the number is not known
   * yet — the failure mode being a captain signing somebody they cannot fit.
   */
  total: number;
  unrated: number;
  /** Cap minus total, or null where no cap applies. Negative means over. */
  headroom: number | null;
}

export interface SquadListDoc {
  teams: ListedTeam[];
  teamCount: number;
  playerCount: number;
  cap: number | null;
  /**
   * Whether this league grades players into tiers instead of rating them.
   *
   * Vitalli and Premier Pearls do. Their squads have no seeds at all, so a
   * squad total is not a small number or an unknown one — it is not a thing
   * that exists. The first version of this printed "Squad total 0.00 · 10
   * unrated" against every house in both leagues, which is what having only
   * one idea of a player's grade does to a league that uses the other.
   */
  tiered: boolean;
  /** Squads at or over the cap, worth a captain's attention before a signing. */
  tight: ListedTeam[];
}

/**
 * How close to the cap counts as worth flagging.
 *
 * A tenth. Kiss My Aces sits at 23.97 against 24.00 and Bed Shop Dream Team at
 * exactly 24.00 — neither is breaking a rule, and both mean the next signing is
 * arithmetic rather than judgement. A list that shows the total but not how
 * little room is left makes the reader do that subtraction twelve times.
 */
const TIGHT = 0.1;

/** Seed first, because the cap is what a squad list is checked against. */
function bySeed(a: ListedPlayer, b: ListedPlayer): number {
  if (a.rating === null && b.rating === null) return a.name.localeCompare(b.name);
  // Unrated last rather than first: they are the players a reader has no number
  // for, and burying them at the top would push the seeds people came for down.
  if (a.rating === null) return 1;
  if (b.rating === null) return -1;
  return b.rating - a.rating || a.name.localeCompare(b.name);
}

/**
 * Build the document from the season's teams.
 *
 * Numbering runs across the whole list rather than restarting per team, which
 * is how the league's own list reads and is what makes "number 93" a thing one
 * person can say to another.
 */
export function squadList(
  teams: readonly Team[],
  cap: number | null = null,
  tiered = false,
): SquadListDoc {
  /*
   * Resolved ONCE, here, rather than at each place that reads it.
   *
   * A tiered league has no seeds, so it can have no cap to be measured against.
   * That was first written into the returned `cap` alone and the per-team
   * headroom went on being computed from the raw one — so the document reported
   * no cap while every house carried a headroom against it. One value, decided
   * in one place, is what stops the two disagreeing.
   */
  const effective = tiered ? null : cap;
  let n = 0;
  const listed = teams.map((team): ListedTeam => {
    const players = team.players
      .map(
        (p: Player): ListedPlayer => ({
          number: 0,
          name: p.name,
          rating: p.rating,
          tier: p.tier,
          substitute: !p.carriesForward,
        }),
      )
      /*
       * A tiered league is listed alphabetically, because there is no seed to
       * order by. Sorting it by rating would put every player in an arbitrary
       * order that looked deliberate — the reader would take the top of each
       * house for its strongest player.
       */
      .sort(tiered ? (a, b) => a.name.localeCompare(b.name) : bySeed)
      .map((p) => ({ ...p, number: (n += 1) }));

    const rated = players.filter((p) => p.rating !== null);
    /*
     * Rounded to the hundredth the seeds are given in. Summing 2.83 + 2.76 +
     * 1.70 in binary floating point gives 7.290000000000001, and a squad list
     * that prints a total like that invites the reader to distrust the rest of
     * the page.
     */
    const total = Math.round(rated.reduce((sum, p) => sum + (p.rating as number), 0) * 100) / 100;

    return {
      id: team.id,
      name: team.name,
      logoUrl: team.logoUrl,
      colour: team.colour,
      players,
      total,
      unrated: players.length - rated.length,
      headroom: effective === null ? null : Math.round((effective - total) * 100) / 100,
    };
  });

  return {
    teams: listed,
    teamCount: listed.length,
    playerCount: n,
    cap: effective,
    tiered,
    /*
     * A squad with an unrated player is never called tight, however close its
     * total sits: the total is incomplete, so the headroom is not a fact about
     * the squad. Saying "0.03 left" when a player has no seed yet would be
     * inventing certainty out of a missing number.
     */
    tight:
      effective === null
        ? []
        : listed.filter((t) => t.unrated === 0 && (t.headroom as number) <= TIGHT),
  };
}
